/**
 * KategorieGruppe — auf-/zuklappbare Kategorie-Gruppe der Warnliste (Plan 02-05 Task 2).
 * Struktur 1:1 aus dem Vorbild-Programm uebernommen (Header-Checkbox indeterminate,
 * Auf-/Zuklappen, Bulk-Delegation an Parent) — Deckungsbeitrags-/Zielwert-Anzeige
 * entfernt, keine Anwendung fuer artikelleichen.
 */
import { useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { KandidatRow, KategorieGruppe as KategorieGruppeType } from '../types';
import LeichenZeile from './LeichenZeile';

interface KategorieGruppeProps {
  gruppe: KategorieGruppeType;
  expanded: boolean;
  onToggleExpanded: () => void;
  selectedIds: Set<number>;
  onToggleItem: (row: KandidatRow) => void;
  onToggleGroup: (gruppe: KategorieGruppeType, select: boolean) => void;
  onSnoozeEinzeln: (itemId: number) => void;
  onAufheben: (itemId: number) => void;
  onWhitelistEinzeln: (itemId: number) => void;
}

export default function KategorieGruppe({
  gruppe,
  expanded,
  onToggleExpanded,
  selectedIds,
  onToggleItem,
  onToggleGroup,
  onSnoozeEinzeln,
  onAufheben,
  onWhitelistEinzeln,
}: KategorieGruppeProps) {
  const alleIds = gruppe.kandidaten.map((k) => k.itemId);
  const ausgewaehlteAnzahl = alleIds.filter((id) => selectedIds.has(id)).length;
  const alleAusgewaehlt = alleIds.length > 0 && ausgewaehlteAnzahl === alleIds.length;
  const teilweiseAusgewaehlt = ausgewaehlteAnzahl > 0 && !alleAusgewaehlt;

  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (headerCheckboxRef.current) headerCheckboxRef.current.indeterminate = teilweiseAusgewaehlt;
  }, [teilweiseAusgewaehlt]);

  return (
    <div className="rounded-lg border border-[#DAD9D9] bg-white overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 bg-[#DAD9D9]/20">
        <input
          type="checkbox"
          ref={headerCheckboxRef}
          checked={alleAusgewaehlt}
          onChange={(e) => onToggleGroup(gruppe, e.target.checked)}
          aria-label={`Alle Treffer in ${gruppe.kategorieName} auswaehlen`}
          className="h-4 w-4"
        />
        <button type="button" onClick={onToggleExpanded} className="flex items-center gap-2 flex-1 min-w-0 text-left">
          {expanded ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
          <span className="font-bold text-[#1A1A1A] truncate">{gruppe.kategorieName}</span>
        </button>

        <span className="text-xs text-gray-600 whitespace-nowrap">{gruppe.trefferzahl} Treffer</span>
      </div>

      {expanded && (
        <div>
          {gruppe.kandidaten.map((row) => (
            <LeichenZeile
              key={row.itemId}
              row={row}
              selected={selectedIds.has(row.itemId)}
              onToggleSelect={() => onToggleItem(row)}
              onSnoozeEinzeln={onSnoozeEinzeln}
              onAufheben={onAufheben}
              onWhitelistEinzeln={onWhitelistEinzeln}
            />
          ))}
        </div>
      )}
    </div>
  );
}
